/**
 * INVOICE-SENT-01 (Seth 2026-09-17: "the ability for the office side to see if
 * the tech sent over the invoice to the customer … anything not sent I'd want
 * the office to know so they can review and send").
 *
 * No flag anyone sets by hand. Every invoice email — the tech's on Complete,
 * the office's from the invoice screen — goes through the ONE outbox with
 * `trigger_kind = 'invoice'` and `trigger_ref` = the invoice (a Pegasus invoice
 * id, or `esc:<no>` for an ESC invoice emailed from Pegasus). The outbox row
 * carries the honest state: held (comms off), queued, sent + when, failed +
 * why, suppressed (the customer opted out). This module reads that back:
 *
 *   useInvoiceSendStatuses(refs)   → one status per invoice, batched
 *   useUnsentBillableInvoices()    → issued Pegasus invoices with money on
 *                                    them and NO send in the system's hands
 *   <InvoiceSentChip />            → the one chip every invoice surface shows
 *
 * The rule (ServiceTitan / Jobber both do this): the invoice is "Sent" when a
 * send actually went out; everything else is a plain "Not sent" the office
 * can act on. Opens are not tracked here on purpose — Apple Mail and Gmail
 * pre-load images, so "opened" would lie.
 */
import { useQuery } from "@tanstack/react-query";
import { Mail, MailCheck, MailWarning, MailX, Clock } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { displayCustomerName } from "@/lib/format";

export type SendState = "sent" | "queued" | "held" | "failed" | "suppressed" | "none";

export interface InvoiceSendStatus {
  state: SendState;
  /** Where it went (or was meant to go). */
  to: string | null;
  /** sent_at for a sent row; created_at otherwise. */
  at: string | null;
  /** The failure / suppression reason, when the engine gave one. */
  detail: string | null;
  /** Who queued it — the tech's login on a phone send, the office's otherwise. */
  by: string | null;
  /** How many sends are on record for this invoice (a resend adds one). */
  count: number;
}

interface OutboxLite {
  trigger_ref: string | null;
  status: string;
  status_detail: string | null;
  sent_at: string | null;
  to_address: string | null;
  created_at: string;
  created_by: string | null;
}

const NONE: InvoiceSendStatus = { state: "none", to: null, at: null, detail: null, by: null, count: 0 };

/** Fold an invoice's outbox rows (newest first) into one status: any real send
 *  wins; otherwise the newest row says where things stand. */
function fold(rows: OutboxLite[]): InvoiceSendStatus {
  if (rows.length === 0) return NONE;
  const sent = rows.filter((r) => r.status === "sent").sort((a, b) => (b.sent_at ?? "").localeCompare(a.sent_at ?? ""))[0];
  if (sent) return { state: "sent", to: sent.to_address, at: sent.sent_at ?? sent.created_at, detail: null, by: sent.created_by, count: rows.length };
  const latest = rows[0];
  const state: SendState =
    latest.status === "queued" ? "queued"
      : latest.status === "held" ? "held"
        : latest.status === "failed" ? "failed"
          : latest.status === "suppressed" ? "suppressed"
            : "none"; // cancelled = as if never sent
  return { state, to: latest.to_address, at: latest.created_at, detail: latest.status_detail, by: latest.created_by, count: rows.length };
}

async function fetchStatuses(refs: string[]): Promise<Map<string, InvoiceSendStatus>> {
  const out = new Map<string, InvoiceSendStatus>();
  const byRef = new Map<string, OutboxLite[]>();
  for (let i = 0; i < refs.length; i += 200) {
    const chunk = refs.slice(i, i + 200);
    const { data, error } = await supabase
      .from("pegasus_outbox")
      .select("trigger_ref, status, status_detail, sent_at, to_address, created_at, created_by")
      .eq("trigger_kind", "invoice")
      .in("trigger_ref", chunk)
      .order("created_at", { ascending: false });
    if (error) throw error;
    for (const r of (data ?? []) as OutboxLite[]) {
      if (!r.trigger_ref) continue;
      const list = byRef.get(r.trigger_ref);
      if (list) list.push(r);
      else byRef.set(r.trigger_ref, [r]);
    }
  }
  for (const ref of refs) out.set(ref, fold(byRef.get(ref) ?? []));
  return out;
}

/** One send status per invoice ref (Pegasus invoice id or `esc:<no>`), batched. */
export function useInvoiceSendStatuses(refs: (string | null | undefined)[]) {
  const clean = Array.from(new Set(refs.filter((r): r is string => !!r))).sort();
  return useQuery({
    queryKey: ["invoice_send_status", clean.join(",")],
    enabled: clean.length > 0,
    staleTime: 60 * 1000,
    queryFn: () => fetchStatuses(clean),
  });
}

export interface UnsentInvoice {
  id: string;
  invoiceNo: string;
  custno: string | null;
  customerName: string | null;
  total: number;
  date: string | null;
  technician: string | null;
  /** The address the tech typed (or the record's) — null = the tech left it blank. */
  recipientEmail: string | null;
  status: InvoiceSendStatus;
}

/** Issued Pegasus invoices from the last `days` with money on them and no send
 *  the system is handling (never queued, or the last try failed / was
 *  suppressed / cancelled). Held and queued rows count as "in hand" — the
 *  engine owns those, not the office. */
export function useUnsentBillableInvoices(days = 30) {
  return useQuery({
    queryKey: ["unsent_billable_invoices", days],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<UnsentInvoice[]> => {
      const since = new Date(Date.now() - days * 86400000).toISOString();
      const { data, error } = await supabase
        .from("pegasus_job_invoice")
        .select("id, invoice_no, dispatch_no, custno, total, technician, completed_date, created_at, recipient_email")
        .in("status", ["issued", "finalized"])
        .gt("total", 0)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      type Row = { id: string; invoice_no: string | null; dispatch_no: string | null; custno: string | null; total: number | null; technician: string | null; completed_date: string | null; created_at: string; recipient_email: string | null };
      const rows = (data ?? []) as Row[];
      if (rows.length === 0) return [];
      const statuses = await fetchStatuses(rows.map((r) => r.id));
      const unsent = rows.filter((r) => {
        const s = statuses.get(r.id)?.state ?? "none";
        return s === "none" || s === "failed" || s === "suppressed";
      });
      if (unsent.length === 0) return [];
      const custnos = Array.from(new Set(unsent.map((r) => r.custno).filter((c): c is string => !!c)));
      const names = new Map<string, string>();
      if (custnos.length > 0) {
        const [{ data: m }, { data: p }] = await Promise.all([
          supabase.from("mirror_customer").select("custno, fullname").in("custno", custnos.slice(0, 300)),
          supabase.from("pegasus_customer").select("custno, fullname").in("custno", custnos.slice(0, 300)),
        ]);
        for (const r of [...((m ?? []) as { custno: string; fullname: string | null }[]), ...((p ?? []) as { custno: string; fullname: string | null }[])]) {
          if (r.fullname && !names.has(r.custno)) names.set(r.custno, displayCustomerName(null, r.fullname));
        }
      }
      return unsent.map((r) => ({
        id: r.id,
        invoiceNo: r.invoice_no || `SH-${r.dispatch_no || r.id.slice(0, 6).toUpperCase()}`,
        custno: r.custno,
        customerName: r.custno ? names.get(r.custno) ?? null : null,
        total: Number(r.total) || 0,
        date: r.completed_date ?? r.created_at.slice(0, 10),
        technician: r.technician,
        recipientEmail: r.recipient_email,
        status: statuses.get(r.id) ?? NONE,
      }));
    },
  });
}

const fmtWhen = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`;
};

/** The one chip: "Sent · laura@… · Sep 17, 3:41 PM" and friends. `onSend`
 *  adds the office's action (Email it / Send again) right on the chip. */
export function InvoiceSentChip({
  status,
  size = "sm",
  onSend,
  sendLabel,
  className,
}: {
  status: InvoiceSendStatus | undefined;
  size?: "xs" | "sm";
  onSend?: () => void;
  sendLabel?: string;
  className?: string;
}) {
  const s = status ?? NONE;
  const base = cn(
    "inline-flex max-w-full items-center gap-1 rounded-full font-semibold",
    size === "xs" ? "px-1.5 py-px text-[10.5px]" : "px-2 py-0.5 text-[11.5px]",
  );
  const look: Record<SendState, { cls: string; Icon: typeof Mail; text: string; title: string }> = {
    sent: { cls: "bg-emerald-50 text-emerald-700", Icon: MailCheck, text: `Sent${s.to ? ` · ${s.to}` : ""}${s.at ? ` · ${fmtWhen(s.at)}` : ""}`, title: `Emailed to the customer${s.by ? ` by ${s.by}` : ""}${s.count > 1 ? ` · ${s.count} sends on record` : ""}` },
    queued: { cls: "bg-sky-50 text-sky-700", Icon: Clock, text: `Sending${s.to ? ` · ${s.to}` : ""}`, title: "Queued — goes out within about ten minutes" },
    held: { cls: "bg-amber-50 text-amber-700", Icon: Clock, text: `Held${s.to ? ` · ${s.to}` : ""}`, title: "Written and parked — customer emails are switched off while Pegasus is in testing (comms_live). Nothing reached the customer." },
    failed: { cls: "bg-rose-50 text-rose-700", Icon: MailWarning, text: `Failed${s.detail ? ` · ${s.detail}` : ""}`, title: `The send failed${s.to ? ` (${s.to})` : ""}${s.detail ? `: ${s.detail}` : ""} — send it again or call.` },
    suppressed: { cls: "bg-rose-50 text-rose-700", Icon: MailX, text: "Not sent · customer opted out of email", title: "The customer asked for no email — mail it or call." },
    none: { cls: "bg-slate-100 text-slate-500", Icon: Mail, text: "Not sent", title: "No invoice email on record — the tech left the email blank, or nobody has sent it yet." },
  };
  const L = look[s.state];
  const actionable = s.state === "none" || s.state === "failed" || s.state === "suppressed" || s.state === "sent";
  return (
    <span className={cn("inline-flex max-w-full items-center gap-1.5", className)}>
      <span className={cn(base, L.cls)} title={L.title}>
        <L.Icon className={size === "xs" ? "h-3 w-3 shrink-0" : "h-3.5 w-3.5 shrink-0"} />
        <span className="truncate">{L.text}</span>
      </span>
      {onSend && actionable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onSend(); }}
          className={cn(
            "shrink-0 rounded-md border px-1.5 font-semibold transition-colors",
            size === "xs" ? "py-px text-[10.5px]" : "py-0.5 text-[11.5px]",
            s.state === "sent" ? "border-hairline bg-white text-slate-500 hover:bg-slate-50" : "border-brand-200 bg-white text-brand-700 hover:bg-brand-50",
          )}
        >
          {sendLabel ?? (s.state === "sent" ? "Send again" : "Email it")}
        </button>
      )}
    </span>
  );
}
